// src/screens/chatScreen/Chat.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  ArrowLeft,
  Send,
  Check,
  X,
  Trash2,
  EyeOff,
  Menu,
  Settings,
  ChevronRight,
  ChevronDown,
  Flag,
  MessageSquare,
} from "lucide-react";
import { useNavigate, useOutletContext } from "react-router-dom";

import "./Chat.css";

import {
  calculateChatProfileAge,
  getReadableTextColor,
  normalizeChatFontSize,
  tintWithWhite,
  useChatData,
} from "./chat.functions";
import { Button } from "../../components/primitives";
import { formatConversationTimestamp } from "../../components/conversation-list/conversation-list.functions";
import { ConfirmActionDialog, ProfileDialog, ReportDialog } from "../../components/dialogs";
import { ProfileAvatar } from "../../components/profile-display";
import { createUserReport } from "../../api/reporting/reporting-service";
import { client } from "../../amplify-client";
import { publishCommunicationEvent } from "../../api/communication";
import { evaluateClientTextModeration } from "../../api/moderation/content-moderation-service";
import { toast } from "../../components/toast";

const FONT_SIZE_OPTIONS = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];
const CHAT_TOPIC_PIN_ENABLED_STORAGE_KEY = "chatTopicPinEnabled";

function ChatInner() {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const signOut = outletContext.signOut;
  const {
    chatReady,
    chatListLoading,
    headerName,
    availableChats,
    acceptingChatRequestIds,
    currentChat,
    selectedChatPk,
    setSelectedChatPk,
    messagesLoading,
    messages,
    input,
    setInput,
    messageModerationWarning,
    clearMessageModerationWarning,
    onKeyDown,
    onSend,
    isSendingMessage,
    me,
    onBack,
    scrollerRef,
    formatMessageTime,
    chatAccentColor,
    myChatColor,
    counterpartChatColor,
    pastChat,
    isPastChat,
    acceptChatRequest,
    denyChatRequest,
    deleteChatRequest,
    hideConversation,
    hideMessage,
  } = useChatData();
  const acceptingRequestIds = Array.isArray(acceptingChatRequestIds)
    ? acceptingChatRequestIds
    : [];

  const [showSettings, setShowSettings] = useState(false);
  const [showFontSizeOptions, setShowFontSizeOptions] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showRequestsPanel, setShowRequestsPanel] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isCounterpartBlocked, setIsCounterpartBlocked] = useState(false);
  const [blockActionLoading, setBlockActionLoading] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: null,
    messageTimestamp: "",
  });
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [activeMessageActionsKey, setActiveMessageActionsKey] = useState(null);
  const [topicPinEnabled, setTopicPinEnabled] = useState(() => {
    const raw = String(localStorage.getItem(CHAT_TOPIC_PIN_ENABLED_STORAGE_KEY) || "").trim();
    if (!raw) return true;
    return raw !== "false";
  });
  const [fontSize, setFontSize] = useState(() => {
    return normalizeChatFontSize(localStorage.getItem("chatFontSize"));
  });
  const settingsRef = useRef(null);
  const menuRef = useRef(null);

  const counterpartUser = useMemo(() => {
    if (currentChat?.isGroup) return null;

    const profile = currentChat?.counterpartProfile || {};
    const cognitoId = String(profile?.cognitoId || currentChat?.counterpartId || "").trim();
    if (!cognitoId) return null;

    return {
      ...profile,
      cognitoId,
      profileName: profile?.profileName || headerName,
      profilePicture: profile?.profilePicture || currentChat?.profilePicture || "",
    };
  }, [currentChat, headerName]);

  useEffect(() => {
    let cancelled = false;

    async function loadBlockedStatus() {
      const targetId = String(counterpartUser?.cognitoId || "").trim();
      if (!targetId) {
        setIsCounterpartBlocked(false);
        return;
      }

      const listBlockedUsersQuery = client?.queries?.listBlockedUsers;
      if (typeof listBlockedUsersQuery !== "function") {
        setIsCounterpartBlocked(false);
        return;
      }

      try {
        const response = await listBlockedUsersQuery({ limit: 200 });
        if (response?.errors?.length) {
          throw new Error(response.errors.map((error) => error.message).join(", "));
        }

        const blockedIds = new Set(
          (Array.isArray(response?.data) ? response.data : [])
            .map((entry) => String(entry?.cognitoId || "").trim())
            .filter(Boolean)
        );

        if (!cancelled) {
          setIsCounterpartBlocked(blockedIds.has(targetId));
        }
      } catch (error) {
        console.error("Failed to load blocked status for chat counterpart:", error);
        if (!cancelled) {
          setIsCounterpartBlocked(false);
        }
      }
    }

    loadBlockedStatus();
    return () => {
      cancelled = true;
    };
  }, [counterpartUser?.cognitoId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettings(false);
        setShowFontSizeOptions(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showSettings || showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettings, showMenu]);

  useEffect(() => {
    if (!showSettings) {
      setShowFontSizeOptions(false);
    }
  }, [showSettings]);

  useEffect(() => {
    setActiveMessageActionsKey(null);
  }, [selectedChatPk, showRequestsPanel]);

  const handleNav = (path) => {
    setShowMenu(false);
    navigate(path);
  };

  const handleSignOut = async () => {
    setShowMenu(false);
    if (typeof signOut === "function") {
      await signOut();
      return;
    }
    navigate("/active", { replace: true });
  };

  const handleFontSizeChange = (size) => {
    const normalizedSize = normalizeChatFontSize(size);
    setFontSize(normalizedSize);
    localStorage.setItem("chatFontSize", normalizedSize);
    setShowFontSizeOptions(false);
    setShowSettings(false);
  };

  const handleTopicPinEnabledChange = () => {
    setTopicPinEnabled((previous) => {
      const nextValue = !previous;
      localStorage.setItem(CHAT_TOPIC_PIN_ENABLED_STORAGE_KEY, nextValue ? "true" : "false");
      return nextValue;
    });
  };

  const getFontSizeClass = () => {
    switch (fontSize) {
      case "small":
        return "chatView__text--small";
      case "large":
        return "chatView__text--large";
      default:
        return "";
    }
  };

  const toggleSettingsMenu = () => {
    setShowMenu(false);
    setShowSettings((previous) => {
      if (previous) {
        setShowFontSizeOptions(false);
        return false;
      }
      return true;
    });
  };

  const toggleHamburgerMenu = () => {
    setShowSettings(false);
    setShowFontSizeOptions(false);
    setShowMenu((previous) => !previous);
  };

  const isPending = currentChat?.status === "pending";
  const isInitiatedByMe = currentChat?.isInitiatedByMe;
  const isAcceptingCurrentRequest = acceptingRequestIds.includes(selectedChatPk);
  const canShowThreadMessages = !isPending;
  const canTypeMessage = Boolean(
    selectedChatPk && currentChat && !isPending && !isAcceptingCurrentRequest
  );
  const latestMyMessageIndex = messages.reduce(
    (latestIdx, message, index) => (message?.isMe ? index : latestIdx),
    -1
  );
  const showLiveChatRail = !isPastChat;
  const acceptedChats = useMemo(
    () =>
      (Array.isArray(availableChats) ? availableChats : [])
        .filter((chat) => chat?.status === "accepted")
        .sort((a, b) => String(b?.lastUpdatedAt || "").localeCompare(String(a?.lastUpdatedAt || ""))),
    [availableChats]
  );
  const pendingChats = useMemo(
    () =>
      (Array.isArray(availableChats) ? availableChats : [])
        .filter((chat) => chat?.status === "pending")
        .sort((a, b) => String(b?.lastUpdatedAt || "").localeCompare(String(a?.lastUpdatedAt || ""))),
    [availableChats]
  );
  const leftRailRows = acceptedChats;
  const requestCount = pendingChats.length;
  const isViewingPendingRequestThread =
    showLiveChatRail && !showRequestsPanel && currentChat?.status === "pending";
  const isRequestsSelected = Boolean(showLiveChatRail && (showRequestsPanel || isViewingPendingRequestThread));
  const isOnline = currentChat?.presenceStatus === "online";
  const pendingCounterpartLabel = String(currentChat?.title || headerName || "the other person").trim();
  const subtitleText = pastChat
    ? "Past conversation"
    : isPending
      ? (isInitiatedByMe
          ? `Awaiting ${pendingCounterpartLabel}'s response`
          : "Pending request")
      : (currentChat?.isGroup ? "Group chat" : (isOnline ? "Online" : "Offline"));
  const subtitleClassName = `chatView__subtitle ${
    !pastChat && !isPending && !currentChat?.isGroup
      ? (isOnline ? "chatView__subtitle--online" : "chatView__subtitle--offline")
      : ""
  }`;
  const requestMessage = String(currentChat?.requestMessage || "").trim();
  const hasRequestMessage = Boolean(requestMessage);
  const requestTimeLabel = formatMessageTime(currentChat?.requestCreatedAt || "");
  const localMessageModeration = useMemo(
    () =>
      evaluateClientTextModeration({
        fieldType: "message",
        text: input,
      }),
    [input]
  );
  const combinedModerationWarning = localMessageModeration.blocked
    ? localMessageModeration.feedback
    : messageModerationWarning;

  const onAcceptRequest = async () => {
    if (!selectedChatPk) return;
    await acceptChatRequest?.(selectedChatPk);
  };

  const openChatFromRail = (chat) => {
    if (!showLiveChatRail) return;
    const chatPk = String(chat?.chatPk || "").trim();
    if (!chatPk) return;
    const chatTitle = String(chat?.title || chat?.name || "chat").trim() || "chat";
    setShowRequestsPanel(false);
    setSelectedChatPk?.(chatPk);
    navigate(`/active/chat/${encodeURIComponent(chatTitle)}`, {
      state: { selectedChatPk: chatPk },
    });
  };

  const openRequestsPanel = () => {
    if (!showLiveChatRail) return;
    setShowRequestsPanel(true);
    setShowMenu(false);
    setShowSettings(false);
    setShowFontSizeOptions(false);
  };

  const handleBackToRequests = () => {
    openRequestsPanel();
  };

  const handleReturnToActive = () => {
    setShowMenu(false);
    setShowSettings(false);
    setShowFontSizeOptions(false);
    onBack();
  };
  const threadBackButtonClassName = isPastChat
    ? "chatView__threadBackAlways"
    : "chatView__threadBackMobile";
  const threadBackButtonAriaLabel = isPastChat
    ? "Back to past conversations"
    : "Back to active screen";

  if (!chatReady) {
    return (
      <div
        className={`chatView ${isPastChat ? "chatView--past" : ""}`}
        style={{ "--chat-accent": chatAccentColor }}
      >
        <div className="chatView__container">
          <div className="chatView__messagesCard">
            <div className="chatView__messagesScroll">
              <div className="chatView__muted">Loading chat…</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const openMessageReport = (message) => {
    const messageSenderId = String(message?.senderId || message?.sender || "").trim();
    const messageSk = String(message?.timestamp || "").trim();
    const messageId = String(message?.messageId || "").trim() || null;

    if (!selectedChatPk || !messageSenderId || !messageSk) {
      return;
    }

    setReportTarget({
      targetType: "MESSAGE",
      targetUserId: messageSenderId,
      chatPk: selectedChatPk,
      messageSk,
      messageId,
      sourceSurface: "CHAT",
      targetLabel: `${headerName} message`,
    });
    setReportDialogOpen(true);
  };

  const openUserReport = () => {
    const targetUserId = String(counterpartUser?.cognitoId || "").trim();
    if (!targetUserId) return;

    setShowSettings(false);
    setShowFontSizeOptions(false);
    setReportTarget({
      targetType: "USER",
      targetUserId,
      sourceSurface: "CHAT",
      targetLabel: headerName || "User",
    });
    setReportDialogOpen(true);
  };

  const closeReportDialog = () => {
    setReportDialogOpen(false);
    setReportTarget(null);
  };

  const submitReport = async ({ reasonCode, reasonText }) => {
    if (!reportTarget?.targetUserId) {
      throw new Error("Report target is missing.");
    }

    if (reportTarget?.targetType === "USER") {
      await createUserReport({
        targetType: "USER",
        targetUserId: reportTarget.targetUserId,
        reasonCode,
        reasonText,
        sourceSurface: reportTarget.sourceSurface || "CHAT",
      });
      toast.success("Report submitted. Our admins will review it.");
      return;
    }

    if (!reportTarget?.chatPk || !reportTarget?.messageSk) {
      throw new Error("Message report context is missing.");
    }

    await createUserReport({
      targetType: reportTarget.targetType,
      targetUserId: reportTarget.targetUserId,
      chatPk: reportTarget.chatPk,
      messageSk: reportTarget.messageSk,
      messageId: reportTarget.messageId,
      reasonCode,
      reasonText,
      sourceSurface: reportTarget.sourceSurface || "CHAT",
    });
    toast.success("Report submitted. Our admins will review it.");
  };

  const onHideMessage = async (messageTimestamp) => {
    const normalizedTimestamp = String(messageTimestamp || "").trim();
    if (!selectedChatPk || !normalizedTimestamp) return;
    await hideMessage?.({ chatPk: selectedChatPk, messageTimestamp: normalizedTimestamp });
  };

  const openHideMessageConfirm = (message) => {
    const messageTimestamp = String(message?.timestamp || "").trim();
    if (!selectedChatPk || !messageTimestamp) return;
    setConfirmDialog({ isOpen: true, type: "message", messageTimestamp });
  };

  const closeConfirmDialog = () => {
    if (confirmSubmitting) return;
    setConfirmDialog({ isOpen: false, type: null, messageTimestamp: "" });
  };

  const confirmHideAction = async () => {
    if (!confirmDialog?.isOpen || confirmSubmitting || confirmDialog.type !== "message") return;

    setConfirmSubmitting(true);
    try {
      await onHideMessage(confirmDialog.messageTimestamp);
      setConfirmDialog({ isOpen: false, type: null, messageTimestamp: "" });
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const onToggleBlockFromProfile = async () => {
    const targetId = String(counterpartUser?.cognitoId || "").trim();
    if (!targetId || blockActionLoading) return;

    const blockMutation = client?.mutations?.blockUser;
    const unblockMutation = client?.mutations?.unblockUser;
    const currentlyBlocked = isCounterpartBlocked;

    if (currentlyBlocked && typeof unblockMutation !== "function") {
      toast.error("Unblock action is unavailable right now.");
      return;
    }
    if (!currentlyBlocked && typeof blockMutation !== "function") {
      toast.error("Block action is unavailable right now.");
      return;
    }

    setBlockActionLoading(true);
    try {
      const response = currentlyBlocked
        ? await unblockMutation({ blockedUserId: targetId })
        : await blockMutation({ blockedUserId: targetId });

      if (response?.errors?.length) {
        throw new Error(response.errors.map((error) => error.message).join(", "));
      }

      setIsCounterpartBlocked(!currentlyBlocked);

      if (currentlyBlocked && selectedChatPk) {
        publishCommunicationEvent({
          type: "chat_visibility_changed",
          chatPk: selectedChatPk,
          status: "visible",
          activityAt: new Date().toISOString(),
        });
      }

      if (!currentlyBlocked) {
        setShowProfileModal(false);
        await hideConversation?.(selectedChatPk);
        onBack();
      }
    } catch (error) {
      console.error("Failed to toggle block state from chat profile:", error);
      toast.error(error?.message || "Unable to update block settings.");
    } finally {
      setBlockActionLoading(false);
    }
  };

  return (
    <div
      className={`chatView ${isPastChat ? "chatView--past" : ""}`}
        style={{ "--chat-accent": chatAccentColor }}
      >
      <div className="chatView__container">
        <div className={`chatView__layout ${showLiveChatRail ? "chatView__layout--withRail" : ""}`}>
          {/* Conversation rail */}
          {showLiveChatRail ? (
            <aside className="chatView__rail" aria-label="Conversations">
              <div className="chatView__railHead">
                <div className="chatView__railTitleRow">
                  <button
                    type="button"
                    className="chatView__railBackLink"
                    onClick={handleReturnToActive}
                    aria-label="Back to active screen"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <h2 className="chatView__railTitle">Active</h2>
                </div>
                <button
                  type="button"
                  className="chatView__railRequestsBtn"
                  onClick={openRequestsPanel}
                  aria-current={isRequestsSelected ? "page" : undefined}
                >
                  <span>Requests</span>
                  <span className="chatView__railMeta">
                    {requestCount > 0 ? (
                      <span className="chatView__railRequestsCount">{requestCount}</span>
                    ) : null}
                    {!isRequestsSelected ? (
                      <span className="chatView__railRequestArrow" aria-hidden="true">
                        <ChevronRight size={14} />
                      </span>
                    ) : null}
                  </span>
                </button>
              </div>

              {chatListLoading && leftRailRows.length === 0 ? (
                <div className="chatView__railEmpty">Loading conversations…</div>
              ) : leftRailRows.length === 0 ? (
                <div className="chatView__railEmpty">
                  <MessageSquare size={16} />
                  <span>No active chats yet.</span>
                </div>
              ) : (
                <div className="chatView__railList">
                  {leftRailRows.map((chat) => {
                    const chatPk = String(chat?.chatPk || "").trim();
                    const isSelected = !isRequestsSelected && chatPk && chatPk === selectedChatPk;
                    const presenceClass = chat?.presenceStatus === "online"
                      ? "chatView__railPresence--online"
                      : "chatView__railPresence--offline";
                    const timestampLabel = formatConversationTimestamp(chat?.lastUpdatedAt);
                    const previewText = String(chat?.previewText || "Open conversation…").trim();
                    const unreadCount = Math.max(0, Number(chat?.unreadCount || 0));
                    const hasUnread = unreadCount > 0;

                    return (
                      <div
                        key={chatPk}
                        className={`chatView__railRow ${
                          isSelected ? "chatView__railRow--selected" : ""
                        } ${
                          hasUnread ? "chatView__railRow--unread" : "chatView__railRow--read"
                        }`}
                      >
                        <button
                          type="button"
                          className="chatView__railRowMain"
                          onClick={() => openChatFromRail(chat)}
                          aria-current={isSelected ? "page" : undefined}
                        >
                          <div className="chatView__railAvatar">
                            <ProfileAvatar
                              user={chat}
                              name={chat?.title || "Chat"}
                              className="chatView__railAvatarImg"
                            />
                            {!chat?.isGroup ? (
                              <span className={`chatView__railPresence ${presenceClass}`} aria-hidden="true" />
                            ) : null}
                          </div>

                          <div className="chatView__railBody">
                            <div className="chatView__railTop">
                              <span className={`chatView__railName ${
                                hasUnread ? "chatView__railName--unread" : ""
                              }`}>
                                {chat?.title || "Chat"}
                              </span>
                              <span className="chatView__railMeta">
                                {timestampLabel ? <span className="chatView__railTime">{timestampLabel}</span> : null}
                                {hasUnread ? (
                                  <span className="chatView__railUnreadBadge" aria-label={`${unreadCount} unread`}>
                                    {unreadCount}
                                  </span>
                                ) : null}
                              </span>
                            </div>
                            <div className={`chatView__railPreview ${
                              hasUnread ? "chatView__railPreview--unread" : ""
                            }`}>
                              {previewText}
                            </div>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </aside>
          ) : null}

          <div className="chatView__thread">
            {/* Requests panel */}
            {showRequestsPanel ? (
              <>
                <div className="chatView__headerCard">
                  <div className="chatView__headerLeft">
                    <div className="chatView__headerMeta">
                      <div className="chatView__title">Requests</div>
                    </div>
                  </div>

                  <div className="chatView__headerRight">
                    <div className="chatView__menuWrap" ref={menuRef}>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Menu"
                        aria-haspopup="menu"
                        aria-expanded={showMenu}
                        className="chatView__iconBtn"
                        onClick={toggleHamburgerMenu}
                      >
                        <Menu size={18} />
                      </Button>

                      {showMenu ? (
                        <div className="chatView__menuDropdown">
                          <button className="chatView__menuItem" onClick={() => handleNav("/edit-profile")}>
                            Update Profile
                          </button>
                          <button className="chatView__menuItem" onClick={() => handleNav("/settings")}>
                            Settings
                          </button>
                          <button className="chatView__menuItem" onClick={() => handleNav("/contact")}>
                            Contact Us
                          </button>
                          <button
                            className="chatView__menuItem chatView__menuItem--danger"
                            onClick={handleSignOut}
                          >
                            Sign out
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="chatView__requestsPanel">
                  {pendingChats.length === 0 ? (
                    <div className="chatView__muted">No pending requests right now.</div>
                  ) : (
                    <div className="chatView__requestsList">
                      {pendingChats.map((requestChat) => {
                        const requestChatPk = String(requestChat?.chatPk || "").trim();
                        const isIncomingRequest = !requestChat?.isInitiatedByMe;
                        const isOutgoingRequest = requestChat?.isInitiatedByMe;
                        const isSelectedRequest = requestChatPk === selectedChatPk;
                        const timestampLabel = formatConversationTimestamp(requestChat?.lastUpdatedAt);
                        const previewText = isIncomingRequest
                          ? "Sent you a conversation request."
                          : "Waiting for them to accept your request.";
                        const requestTopicText = String(
                          requestChat?.requestMessage || requestChat?.previewText || ""
                        ).trim();

                        return (
                          <div
                            key={requestChatPk}
                            className={`chatView__requestCard ${
                              isIncomingRequest
                                ? "chatView__requestCard--incoming"
                                : "chatView__requestCard--outgoing"
                            } ${
                              isSelectedRequest ? "chatView__requestCard--selected" : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="chatView__requestCardMain"
                              onClick={() => openChatFromRail(requestChat)}
                            >
                              <div className="chatView__requestCardAvatar">
                                <ProfileAvatar
                                  user={requestChat}
                                  name={requestChat?.title || "Request"}
                                  className="chatView__requestCardAvatarImg"
                                />
                              </div>
                              <div className="chatView__requestCardBody">
                                <div className="chatView__requestCardTop">
                                  <span className="chatView__requestCardName">
                                    {requestChat?.title || "Request"}
                                  </span>
                                  <span className="chatView__requestCardMeta">
                                    {timestampLabel || "Open"}
                                    <ChevronRight size={14} />
                                  </span>
                                </div>
                                <div className="chatView__requestCardPreview">{previewText}</div>
                                {requestTopicText ? (
                                  <div className="chatView__requestCardTopic">{requestTopicText}</div>
                                ) : null}
                              </div>
                            </button>

                            {isIncomingRequest ? (
                              <div className="chatView__requestCardActions">
                                <button
                                  type="button"
                                  className="chatView__requestActionBtn chatView__requestActionBtn--accept"
                                  onClick={async () => {
                                    try {
                                      await acceptChatRequest?.(requestChatPk);
                                      setShowRequestsPanel(true);
                                    } catch (error) {
                                      toast.error(error?.message || "Failed to accept request. Please try again.");
                                    }
                                  }}
                                  disabled={acceptingRequestIds.includes(requestChatPk)}
                                  aria-busy={acceptingRequestIds.includes(requestChatPk)}
                                >
                                  {acceptingRequestIds.includes(requestChatPk)
                                    ? "Accepting..."
                                    : "Accept"}
                                </button>
                                <button
                                  type="button"
                                  className="chatView__requestActionBtn"
                                  onClick={() => denyChatRequest?.(requestChatPk)}
                                >
                                  Ignore
                                </button>
                              </div>
                            ) : null}

                            {isOutgoingRequest ? (
                              <div className="chatView__requestCardActions">
                                <button
                                  type="button"
                                  className="chatView__requestActionBtn"
                                  onClick={() => deleteChatRequest?.(requestChatPk)}
                                >
                                  Cancel Request
                                </button>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="chatView__headerCard">
              <div className="chatView__headerLeft">
                {isViewingPendingRequestThread ? (
                  <button
                    type="button"
                    onClick={handleBackToRequests}
                    aria-label="Back to requests"
                    className="chatView__requestThreadBack"
                  >
                    <ArrowLeft size={18} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleReturnToActive}
                    aria-label={threadBackButtonAriaLabel}
                    className={threadBackButtonClassName}
                  >
                    <ArrowLeft size={18} />
                  </button>
                )}

                <button
                  type="button"
                  className="chatView__profileTrigger"
                  onClick={() => setShowProfileModal(true)}
                  disabled={!counterpartUser}
                  aria-label={counterpartUser ? `View ${headerName} profile` : "Profile unavailable"}
                >
                  <div className="chatView__avatar">
                    <ProfileAvatar user={currentChat} name={headerName} className="chatView__avatarImg" />
                    {!pastChat && !currentChat?.isGroup ? (
                      <span
                        className={`chatView__avatarPresence ${
                          isOnline ? "chatView__avatarPresence--online" : "chatView__avatarPresence--offline"
                        }`}
                        aria-hidden="true"
                      />
                    ) : null}
                  </div>

                  <div className="chatView__headerMeta">
                    <div className="chatView__title">{headerName}</div>
                    <div className={subtitleClassName}>{subtitleText}</div>
                  </div>
                </button>
              </div>

              <div className="chatView__headerRight">
                <div className="chatView__menuWrap" ref={settingsRef}>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Chat settings"
                    aria-haspopup="menu"
                    aria-expanded={showSettings}
                    className="chatView__iconBtn"
                    onClick={toggleSettingsMenu}
                  >
                    <Settings size={18} />
                  </Button>

                  {showSettings ? (
                    <div className="chatView__settingsDropdown">
                      <button
                        type="button"
                        className="chatView__settingsOption chatView__settingsOption--toggle"
                        onClick={handleTopicPinEnabledChange}
                        role="switch"
                        aria-checked={topicPinEnabled}
                      >
                        Conversation Topic
                        <span
                          className={`chatView__settingsSwitch ${
                            topicPinEnabled ? "chatView__settingsSwitch--on" : ""
                          }`}
                          aria-hidden="true"
                        >
                          <span className="chatView__settingsSwitchKnob" />
                        </span>
                      </button>
                      <button
                        type="button"
                        className="chatView__settingsOption"
                        onClick={openUserReport}
                      >
                        Report User
                      </button>
                      <button
                        type="button"
                        className="chatView__settingsOption"
                        onClick={() => setShowFontSizeOptions((previous) => !previous)}
                        aria-expanded={showFontSizeOptions}
                      >
                        Font Size
                        {showFontSizeOptions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                      {showFontSizeOptions ? (
                        <div className="chatView__settingsSubmenu">
                          {FONT_SIZE_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              className={`chatView__settingsOption chatView__settingsOption--sub ${
                                fontSize === option.value ? "chatView__settingsOption--active" : ""
                              }`}
                              onClick={() => handleFontSizeChange(option.value)}
                            >
                              {option.label}
                              {fontSize === option.value ? <Check size={16} /> : null}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="chatView__menuWrap" ref={menuRef}>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Menu"
                    className="chatView__iconBtn"
                    onClick={toggleHamburgerMenu}
                  >
                    <Menu size={18} />
                  </Button>

                  {showMenu ? (
                    <div className="chatView__menuDropdown">
                      <button className="chatView__menuItem" onClick={() => handleNav("/edit-profile")}>
                        Update Profile
                      </button>
                      <button className="chatView__menuItem" onClick={() => handleNav("/settings")}>
                        Settings
                      </button>
                      <button className="chatView__menuItem" onClick={() => handleNav("/contact")}>
                        Contact Us
                      </button>
                      <button
                        className="chatView__menuItem chatView__menuItem--danger"
                        onClick={handleSignOut}
                      >
                        Sign out
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Pending request banners */}
            {isPending && !isInitiatedByMe ? (
              <div className="chatView__requestBanner">
                <div className="chatView__requestText">
                  {headerName} sent you a conversation request
                </div>
                <div className="chatView__requestActions">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onAcceptRequest}
                    disabled={isAcceptingCurrentRequest}
                    aria-busy={isAcceptingCurrentRequest}
                  >
                    <Check size={16} />
                    {isAcceptingCurrentRequest ? "Accepting..." : "Accept"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => denyChatRequest(selectedChatPk)}
                  >
                    <X size={16} />
                    Deny
                  </Button>
                </div>
              </div>
            ) : null}

            {isPending && isInitiatedByMe ? (
              <div className="chatView__requestBanner chatView__requestBanner--sent">
                <div className="chatView__requestText">
                  Your conversation request and message were sent. We will notify you when {headerName} accepts.
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteChatRequest(selectedChatPk)}
                >
                  <Trash2 size={16} />
                  Delete Request
                </Button>
              </div>
            ) : null}

            {/* Message thread */}
            <div className="chatView__messagesCard">
              {hasRequestMessage && topicPinEnabled ? (
                <div className="chatView__requestContextOverlay">
                  <div className="chatView__requestContext">
                    <div className="chatView__requestContextHead">
                      <span className="chatView__requestContextLabel">Conversation topic</span>
                    </div>
                    <div className={`chatView__requestContextText ${getFontSizeClass()}`}>
                      {requestMessage}
                    </div>
                    {requestTimeLabel ? (
                      <div className="chatView__requestContextTime">{requestTimeLabel}</div>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div ref={scrollerRef} className="chatView__messagesScroll">
                {messagesLoading ? (
                  <div className="chatView__muted">Loading messages…</div>
                ) : !selectedChatPk ? (
                  <div className="chatView__muted">
                    Couldn’t find this chat. Go back and open it from {isPastChat ? "Past" : "Active"}.
                  </div>
                ) : (
                  <div className="chatView__stack">
                    {!canShowThreadMessages ? (
                      <div className="chatView__muted">
                        {isPending
                          ? (isInitiatedByMe
                              ? "Your request message has already been delivered. We will notify you once they accept."
                              : "Accept this request to start messaging")
                          : "No messages yet."}
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="chatView__muted">No messages yet.</div>
                    ) : (
                      <>
                        {messages.map((msg, index) => {
                          const messageKey = msg.localId || msg.timestamp || `${index}-${msg.text}`;
                          const isMe = !!msg.isMe;
                          const timeLabel = formatMessageTime(msg.timestamp ?? msg.time);
                          const isLatestOutgoing = isMe && index === latestMyMessageIndex;
                          const status = String(msg.deliveryStatus || "").trim();
                          const resolvedStatus = isLatestOutgoing ? (status || "delivered") : status;
                          const showDeliveryStatus = isLatestOutgoing && Boolean(resolvedStatus);
                          const canReportMessage = !isMe && !msg?.isOptimistic && Boolean(selectedChatPk);
                          const canHideMessage = !msg?.isOptimistic && Boolean(selectedChatPk);
                          const hasMessageActions = canReportMessage || canHideMessage;
                          const bubbleColor = isMe ? myChatColor : counterpartChatColor;
                          const bubbleBackground = isMe ? bubbleColor : tintWithWhite(bubbleColor);
                          const bubbleTextColor = getReadableTextColor(bubbleBackground);
                          const bubbleStyle = isMe
                            ? {
                                backgroundColor: bubbleBackground,
                                color: bubbleTextColor,
                              }
                            : {
                                backgroundColor: bubbleBackground,
                                color: bubbleTextColor,
                                borderColor: bubbleColor,
                                borderStyle: "solid",
                                borderWidth: 1,
                              };
                          let deliveryLabel = "";
                          if (resolvedStatus === "sent") deliveryLabel = "Sent";
                          if (resolvedStatus === "delivered") deliveryLabel = "Delivered";
                          if (resolvedStatus === "failed") deliveryLabel = "Failed to send";

                          return (
                            <div
                              key={messageKey}
                              className={`chatView__row ${
                                isMe ? "chatView__row--me" : "chatView__row--them"
                              }`}
                            >
                              <div className={`chatView__messageWrap ${
                                hasMessageActions ? "chatView__messageWrap--interactive" : ""
                              } ${
                                isMe ? "chatView__messageWrap--me" : "chatView__messageWrap--them"
                              } ${
                                hasMessageActions && activeMessageActionsKey === messageKey
                                  ? "chatView__messageWrap--actionsActive"
                                  : ""
                              }`}
                                onMouseLeave={() => {
                                  if (!hasMessageActions) return;
                                  setActiveMessageActionsKey((previous) =>
                                    previous === messageKey ? null : previous
                                  );
                                }}
                                onFocus={() => {
                                  if (!hasMessageActions) return;
                                  setActiveMessageActionsKey(messageKey);
                                }}
                                onBlur={(e) => {
                                  if (!hasMessageActions) return;
                                  // Only hide when focus leaves the entire wrap (not moving between children).
                                  if (e.currentTarget.contains(e.relatedTarget)) return;
                                  setActiveMessageActionsKey((previous) =>
                                    previous === messageKey ? null : previous
                                  );
                                }}
                              >
                                <div className="chatView__messageContent">
                                  <div
                                    className={`chatView__bubble ${
                                      isMe ? "chatView__bubble--me" : "chatView__bubble--them"
                                    }`}
                                    style={bubbleStyle}
                                    onMouseEnter={() => {
                                      if (!hasMessageActions) return;
                                      setActiveMessageActionsKey(messageKey);
                                    }}
                                  >
                                    <div className={`chatView__text ${getFontSizeClass()}`}>{msg.text}</div>
                                  </div>

                                  {timeLabel ? (
                                    <div className="chatView__timeBelow">{timeLabel}</div>
                                  ) : null}

                                  {showDeliveryStatus ? (
                                    <div className={`chatView__delivery chatView__delivery--${resolvedStatus}`}>
                                      {deliveryLabel}
                                    </div>
                                  ) : null}
                                </div>

                                {hasMessageActions ? (
                                  <div className="chatView__hoverActions">
                                    {canReportMessage ? (
                                      <button
                                        type="button"
                                        className="chatView__hoverIconBtn chatView__hoverIconBtn--report"
                                        onClick={() => openMessageReport(msg)}
                                        aria-label="Report message"
                                        title="Report"
                                      >
                                        <Flag size={14} />
                                      </button>
                                    ) : null}
                                    {canHideMessage ? (
                                      <button
                                        type="button"
                                        className="chatView__hoverIconBtn chatView__hoverIconBtn--hide"
                                        onClick={() => openHideMessageConfirm(msg)}
                                        aria-label="Hide message"
                                        title="Hide"
                                      >
                                        <EyeOff size={14} />
                                      </button>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Message composer */}
            <div className="chatView__inputCard">
              <div className="chatView__inputRow">
                <textarea
                  className="chatView__textarea"
                  aria-label="Type your message"
                  value={input}
                  onChange={(e) => {
                    if (messageModerationWarning) {
                      clearMessageModerationWarning?.();
                    }
                    setInput(e.target.value);
                  }}
                  onKeyDown={onKeyDown}
                  placeholder={
                    !currentChat
                      ? "Loading chat…"
                      : isPending
                        ? (isInitiatedByMe
                            ? `Request sent. Waiting for ${pendingCounterpartLabel} to respond…`
                            : (isAcceptingCurrentRequest
                                ? "Accepting request..."
                                : "Accept request to message"))
                        : "Type your message…"
                  }
                  rows={2}
                  disabled={!canTypeMessage}
                />
                <Button
                  variant="primary"
                  size="icon"
                  onClick={onSend}
                  disabled={
                    !canTypeMessage ||
                    Boolean(localMessageModeration.blocked || messageModerationWarning) ||
                    Boolean(isSendingMessage)
                  }
                  aria-label={isSendingMessage ? "Sending message" : "Send message"}
                  aria-busy={Boolean(isSendingMessage)}
                  className="chatView__sendBtn"
                >
                  <Send size={18} />
                </Button>
              </div>

              {combinedModerationWarning ? (
                <div
                  className="chatView__moderationWarning"
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                >
                  {combinedModerationWarning}
                </div>
              ) : null}

	              <div className="chatView__sendAs">
	                Sending as:{" "}
	                <span className="chatView__sendAsName">{me?.label || "You"}</span>
	              </div>
	            </div>
	          </>
            )}
          </div>
	        </div>
	      </div>

      <ReportDialog
        isOpen={reportDialogOpen}
        title={reportTarget?.targetType === "USER" ? "Report User" : "Report Message"}
        subtitle={
          reportTarget?.targetType === "USER"
            ? "Share why this user should be reviewed."
            : "Share why this message should be reviewed."
        }
        targetLabel={reportTarget?.targetLabel || (reportTarget?.targetType === "USER" ? "User" : "Message")}
        onClose={closeReportDialog}
        onSubmit={submitReport}
      />

      {showProfileModal && counterpartUser && (
        <ProfileDialog
          user={counterpartUser}
          onClose={() => setShowProfileModal(false)}
          calculateAge={calculateChatProfileAge}
          isBlocked={isCounterpartBlocked}
          onToggleBlock={onToggleBlockFromProfile}
          blockActionLoading={blockActionLoading}
          onReportUser={openUserReport}
        />
      )}

      {/* Confirm dialogs */}
      <ConfirmActionDialog
        isOpen={Boolean(confirmDialog?.isOpen)}
        title="Hide Message?"
        message="This message will be hidden only for you. The other person will still see it in their chat. This action can't be undone from here."
        confirmLabel="Hide Message"
        cancelLabel="Cancel"
        onClose={closeConfirmDialog}
        onConfirm={confirmHideAction}
        submitting={confirmSubmitting}
      />
    </div>
  );
}

export default function Chat() {
  return <ChatInner />;
}
